import { useState } from "react";
import {
  EmailTemplateConfigV2,
  EmailTemplateConfig,
  GoogleFont,
  ContentBlock,
  EmailTemplateType,
  isV2Config,
  migrateToV2,
  injectValueLayerBlocks,
  stripRemovedBlocks,
  DEFAULT_CONVERSATION_BLOCKS,
  DEFAULT_CUSTOMER_BLOCKS,
  DEFAULT_MEETING_RECAP_BLOCKS,
  DEFAULT_ESCALATION_ALERT_BLOCKS,
  DEFAULT_CONVERSATION_DIGEST_BLOCKS,
  DEFAULT_CONVERSATION_AUTO_DIGEST_BLOCKS,
  DEFAULT_VIEW_REPORT_BLOCKS,
} from "@/types/email-templates";

const GOOGLE_FONTS: GoogleFont[] = ["Poppins", "Roboto", "Inter", "Lato", "Open Sans"] as const;

// Block types that appear in nearly every template type's default set —
// overlap on these alone doesn't mean a saved config actually belongs to
// this template type. Excluded from hasOwnBlocks's overlap check below.
const GENERIC_BLOCK_TYPES = new Set(["mobile_app_promo", "signature"]);

const DEFAULT_HELP_GENIE_LOGO = "https://s3.ap-southeast-2.amazonaws.com/fs.enterprisedna.co/logos/helpgenie.png";

const defaultConfig: EmailTemplateConfigV2 = {
  version: 2,
  colors: {
    primary: "#4E9CFF",
    accent: "#FF4DB8",
    text: "#1A1A1A",
    background: "#F9FAFC",
    headerColor: undefined,
  },
  fonts: {
    headingSize: 32,
    googleFont: "Poppins",
  },
  branding: {
    logoUrl: DEFAULT_HELP_GENIE_LOGO,
    companyName: "Help Genie",
  },
  buttons: {
    buttonStyle: 'rounded',
    borderRadius: 8,
    shadowEnabled: true,
  },
  blocks: DEFAULT_CONVERSATION_BLOCKS,
};

function getDefaultConfigForType(templateType: EmailTemplateType): EmailTemplateConfigV2 {
  const blocks =
    templateType === 'conversation_report'
      ? DEFAULT_CONVERSATION_BLOCKS
      : templateType === 'meeting_recap'
        ? DEFAULT_MEETING_RECAP_BLOCKS
        : templateType === 'escalation_alert'
          ? DEFAULT_ESCALATION_ALERT_BLOCKS
          : templateType === 'conversation_digest'
            ? DEFAULT_CONVERSATION_DIGEST_BLOCKS
            : templateType === 'conversation_auto_digest'
              ? DEFAULT_CONVERSATION_AUTO_DIGEST_BLOCKS
              : templateType === 'view_report'
                ? DEFAULT_VIEW_REPORT_BLOCKS
                : DEFAULT_CUSTOMER_BLOCKS;
  return {
    ...defaultConfig,
    blocks,
  };
}

export const useEmailTemplateConfig = () => {
  const [config, setConfig] = useState<EmailTemplateConfigV2>(defaultConfig);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    colors: false,
    fonts: false,
    branding: false,
    buttons: false,
  });

  /** Load config from DB — auto-migrates V1 to V2. `newConfig` is null when no saved template exists yet. */
  const updateConfig = (newConfig: EmailTemplateConfig | null, templateType: EmailTemplateType = 'conversation_report') => {
    let v2Config: EmailTemplateConfigV2;

    if (!newConfig) {
      v2Config = getDefaultConfigForType(templateType);
    } else if (isV2Config(newConfig)) {
      v2Config = newConfig;
    } else {
      v2Config = migrateToV2(newConfig, templateType);
    }

    console.log("[useEmailTemplateConfig] updateConfig", { templateType, newConfig, v2Config });

    // A saved V2 config may carry a blocks array from a different template
    // type (e.g. an empty/stale shell saved before this type had its own
    // blocks, a config cloned before the type existed, or the agent's shared
    // brand-template row — type: null — falling back for a type it was never
    // customized for). If none of the saved blocks belong to this template's
    // own default set, treat it as unset and seed from the type's defaults
    // instead of just appending the (small) value-layer list on top of the
    // wrong blocks. mobile_app_promo/signature are excluded from this check —
    // GENERIC_BLOCK_TYPES appear in nearly every type's defaults, so overlap
    // on those alone would incorrectly pass a totally-wrong-typed config
    // (e.g. conversation_report's blocks mistaken for conversation_auto_digest's).
    const defaultTypesForTemplate = new Set(
      getDefaultConfigForType(templateType).blocks.map((b) => b.type).filter((t) => !GENERIC_BLOCK_TYPES.has(t)),
    );
    const savedBlocks = stripRemovedBlocks(v2Config.blocks || []);
    const hasOwnBlocks = savedBlocks.some((b) => defaultTypesForTemplate.has(b.type));
    // The defaults keep whatever was saved for the generic blocks (a signature
    // switched on from Branding lives only there), as the renderers do at send time.
    const baseBlocks = hasOwnBlocks
      ? savedBlocks
      : getDefaultConfigForType(templateType).blocks.map((block) => {
          const saved = savedBlocks.find((b) => b.type === block.type);
          return saved ? { ...saved, order: block.order } : block;
        });

    // Mirror the renderer's value-layer auto-inject so any block the email
    // will render also appears in the Report Sections panel for the user to
    // toggle / reorder / rename. Without this, existing tenants only see
    // their original 6 blocks while the email itself renders 12+.
    const blocksWithValueLayer = injectValueLayerBlocks(baseBlocks, templateType);

    const mergedBranding = {
      ...defaultConfig.branding,
      ...v2Config.branding,
      logoUrl: v2Config.branding?.logoUrl || DEFAULT_HELP_GENIE_LOGO,
    };

    setConfig({
      ...getDefaultConfigForType(templateType),
      ...v2Config,
      blocks: blocksWithValueLayer,
      branding: mergedBranding,
    });
  };

  const handleColorChange = (key: string, value: string) => {
    setConfig((prev) => ({
      ...prev,
      colors: { ...prev.colors, [key]: value },
    }));
  };

  const handleFontChange = (key: string, value: string | number) => {
    setConfig((prev) => ({
      ...prev,
      fonts: { ...prev.fonts, [key]: value },
    }));
  };

  const handleGoogleFontChange = (value: string) => {
    if (GOOGLE_FONTS.includes(value as GoogleFont)) {
      handleFontChange("googleFont", value);
    }
  };

  /** Legacy section toggle — maps to block enabled state */
  const handleSectionToggle = (key: string, value: boolean) => {
    const SECTION_TO_BLOCK_TYPE: Record<string, string> = {
      showMetrics: 'metrics',
      showLeadInfo: 'lead_info',
      showTopics: 'topics',
      showInsights: 'insights',
      showTranscript: 'transcript',
      showSummary: 'summary',
    };

    const blockType = SECTION_TO_BLOCK_TYPE[key];
    if (blockType) {
      toggleBlock(blockType, value);
    }
  };

  const handleBrandingChange = (key: string, value: string) => {
    setConfig((prev) => ({
      ...prev,
      branding: { ...prev.branding, [key]: value },
    }));
  };

  const handleButtonChange = (key: string, value: string | number | boolean) => {
    setConfig((prev) => ({
      ...prev,
      buttons: { ...prev.buttons, [key]: value },
    }));
  };

  const resetToDefaults = (templateType: EmailTemplateType = 'conversation_report') => {
    setConfig(getDefaultConfigForType(templateType));
  };

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // --- Block Operations ---

  const toggleBlock = (blockId: string, enabled: boolean) => {
    setConfig((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, enabled } : b
      ),
    }));
  };

  const reorderBlocks = (blocks: ContentBlock[]) => {
    setConfig((prev) => ({
      ...prev,
      blocks: blocks.map((b, i) => ({ ...b, order: i })),
    }));
  };

  const updateBlockLabel = (blockId: string, label: string) => {
    setConfig((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, label: label || undefined } : b
      ),
    }));
  };

  const updateBlockSettings = (blockId: string, settings: Record<string, any>) => {
    setConfig((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, settings: { ...b.settings, ...settings } } : b
      ),
    }));
  };

  const addBlock = (block: ContentBlock) => {
    setConfig((prev) => {
      const maxOrder = prev.blocks.length > 0
        ? Math.max(...prev.blocks.map((b) => b.order))
        : -1;
      return {
        ...prev,
        blocks: [...prev.blocks, { ...block, order: maxOrder + 1 }],
      };
    });
  };

  const removeBlock = (blockId: string) => {
    setConfig((prev) => ({
      ...prev,
      blocks: prev.blocks
        .filter((b) => b.id !== blockId)
        .map((b, i) => ({ ...b, order: i })),
    }));
  };

  return {
    config,
    setConfig,
    updateConfig,
    handleColorChange,
    handleFontChange,
    handleGoogleFontChange,
    handleSectionToggle,
    handleBrandingChange,
    handleButtonChange,
    resetToDefaults,
    openSections,
    toggleSection,
    GOOGLE_FONTS,
    defaultConfig,
    // Block operations
    toggleBlock,
    reorderBlocks,
    updateBlockLabel,
    updateBlockSettings,
    addBlock,
    removeBlock,
  };
};
